import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, User, Church, Users, CalendarPlus, Megaphone, KeyRound, Mail, HandHelping, MessagesSquare } from "lucide-react";
import { Input } from "../components/ui/input";
import AvatarUploader from "../components/AvatarUploader";
import CommunityFeed from "../components/CommunityFeed";
import NotificationsBell from "../components/NotificationsBell";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cls =
  "bg-black border-0 border-b border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-primary h-11 px-0 text-foreground";

export default function Portal() {
  const { user, token, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("community");

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
    if (user?.is_admin) navigate("/admin");
  }, [user, navigate]);

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  const isLeader = user.role === "church_leader";

  const tabs = [
    { key: "community", label: "Community", icon: MessagesSquare },
    { key: "profile", label: "Profile", icon: User },
    ...(isLeader
      ? [
          { key: "church", label: "My Church", icon: Church },
          { key: "members", label: "Members", icon: Users },
          { key: "events", label: "Events", icon: CalendarPlus },
          { key: "announcements", label: "Announcements", icon: Megaphone },
          { key: "registrations", label: "Registrations", icon: HandHelping },
          { key: "prayers", label: "Prayer Requests", icon: Mail },
        ]
      : []),
    { key: "password", label: "Change Password", icon: KeyRound },
  ];

  return (
    <div className="min-h-screen bg-background flex" data-testid="portal-dashboard">
      <aside className="w-72 shrink-0 border-r border-border bg-black min-h-screen p-6 sticky top-0 h-screen overflow-y-auto">
        <div className="flex items-center gap-4 mb-8">
          <AvatarUploader size={56} />
          <div className="min-w-0 flex-1">
            <p className="font-display uppercase text-sm text-foreground truncate" data-testid="portal-user-name">
              {user.name}
            </p>
            <p className="overline text-secondary text-[0.6rem]">{user.role.replace("_", " ")}</p>
          </div>
          <NotificationsBell target="/portal" />
        </div>
        <nav className="space-y-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`portal-tab-${t.key}`}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors ${
                  tab === t.key ? "border-secondary bg-muted/30 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon size={16} />
                <span className="font-body text-sm">{t.label}</span>
              </button>
            );
          })}
          <button
            onClick={logout}
            data-testid="portal-logout"
            className="w-full text-left flex items-center gap-3 px-3 py-2.5 mt-6 border-l-2 border-transparent text-muted-foreground hover:text-primary transition-colors"
          >
            <LogOut size={16} />
            <span className="font-body text-sm">Log Out</span>
          </button>
        </nav>
      </aside>

      <main className="flex-1 p-8 md:p-12 max-w-[1400px]">
        {tab === "community" && (
          <div data-testid="portal-community">
            <div className="mb-8">
              <p className="overline text-primary text-[0.6rem] mb-2">EKG Network</p>
              <h1 className="font-display uppercase text-5xl text-foreground">Community</h1>
              <p className="font-body text-sm text-muted-foreground mt-2">Testimonies, prayer, announcements — for the whole Network.</p>
            </div>
            <CommunityFeed />
          </div>
        )}
        {tab === "profile" && <ProfileTab user={user} refresh={refresh} token={token} />}
        {tab === "church" && isLeader && <MyChurchTab token={token} />}
        {tab === "members" && isLeader && <MembersTab token={token} />}
        {tab === "events" && isLeader && <LeaderListSubmit kind="events" token={token} />}
        {tab === "announcements" && isLeader && <LeaderListSubmit kind="announcements" token={token} />}
        {tab === "registrations" && isLeader && <ReadOnlyList endpoint="leader/registrations" label="Summit Registrations" token={token} columns={["name","email","role"]} />}
        {tab === "prayers" && isLeader && <ReadOnlyList endpoint="leader/prayer-requests" label="Prayer & Info Requests" token={token} columns={["name","email","kind"]} />}
        {tab === "password" && <ChangePasswordTab token={token} />}
      </main>
    </div>
  );
}

function Panel({ title, children, action }) {
  return (
    <div data-testid={`panel-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-end justify-between mb-8">
        <h1 className="font-display uppercase text-5xl text-foreground">{title}</h1>
        {action}
      </div>
      {children}
    </div>
  );
}

function ProfileTab({ user }) {
  return (
    <Panel title="Profile">
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-start">
        <div className="text-center">
          <AvatarUploader size={160} />
          <p className="overline text-secondary text-[0.6rem] mt-4">Tap to change</p>
        </div>
        <div className="space-y-4">
          <FieldRow label="Name" value={user.name} />
          <FieldRow label="Email" value={user.email} />
          <FieldRow label="Role" value={user.role.replace("_", " ")} />
          {user.title && <FieldRow label="Title" value={user.title} />}
          <FieldRow label="Status" value={user.status} />
        </div>
      </div>
    </Panel>
  );
}

function FieldRow({ label, value }) {
  return (
    <div className="border-b border-border pb-3">
      <p className="overline text-muted-foreground text-[0.6rem]">{label}</p>
      <p className="font-body text-lg text-foreground capitalize">{value || "—"}</p>
    </div>
  );
}

function ChangePasswordTab({ token }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (nw !== cf) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: cur, new_password: nw },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Password updated.");
      setCur(""); setNw(""); setCf("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Change Password">
      <form onSubmit={submit} className="space-y-6 max-w-md" data-testid="change-password-form">
        <Input data-testid="cur-pw" type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} className={cls} required />
        <Input data-testid="new-pw" type="password" placeholder="New password (min 8)" value={nw} onChange={(e) => setNw(e.target.value)} className={cls} required />
        <Input data-testid="cf-pw" type="password" placeholder="Confirm new password" value={cf} onChange={(e) => setCf(e.target.value)} className={cls} required />
        <button type="submit" disabled={busy} data-testid="change-password-btn" className="bg-primary text-primary-foreground overline py-4 px-8 hover:bg-primary/85 disabled:opacity-60">
          {busy ? "Saving..." : "Save Password"}
        </button>
      </form>
    </Panel>
  );
}

function MyChurchTab({ token }) {
  const [ch, setCh] = useState(null);
  const [form, setForm] = useState({ name: "", type: "Local Church", address: "", city: "", state: "", pastor: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leader/my-church`, { headers: { Authorization: `Bearer ${token}` } });
      setCh(data);
      setForm({
        name: data.name || "", type: data.type || "Local Church", address: data.address || "",
        city: data.city || "", state: data.state || "", pastor: data.pastor || "",
        email: data.email || "", phone: data.phone || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load church.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.patch(`${API}/leader/my-church`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Church profile updated.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="My Church">
      {!ch ? (
        <p className="font-editorial italic text-muted-foreground">No church is assigned to your account yet.</p>
      ) : (
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl" data-testid="my-church-form">
          {["name","type","address","city","state","pastor","email","phone"].map((k) => (
            <div key={k}>
              <label className="overline text-[0.6rem] text-muted-foreground">{k}</label>
              <Input data-testid={`church-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={cls} />
            </div>
          ))}
          <div className="md:col-span-2">
            <button type="submit" disabled={busy} data-testid="save-my-church" className="bg-primary text-primary-foreground overline py-4 px-8 hover:bg-primary/85 disabled:opacity-60">
              {busy ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}

function MembersTab({ token }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "Member" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leader/members`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/leader/members`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Member added.");
      setForm({ name: "", email: "", phone: "", role: "Member" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await axios.delete(`${API}/leader/members/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Member removed.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <Panel title="Members">
      <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8 items-end" data-testid="add-member-form">
        <Input data-testid="member-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cls} required />
        <Input data-testid="member-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={cls} />
        <Input data-testid="member-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={cls} />
        <Input data-testid="member-role" placeholder="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={cls} />
        <button type="submit" disabled={busy} data-testid="add-member-btn" className="bg-primary text-primary-foreground overline py-3 hover:bg-primary/85 disabled:opacity-60">
          {busy ? "Adding..." : "Add"}
        </button>
      </form>
      <div className="border border-border">
        <div className="grid grid-cols-5 gap-4 px-4 py-3 border-b border-border overline text-[0.6rem] text-secondary bg-muted/20">
          <span>Name</span><span>Email</span><span>Phone</span><span>Role</span><span></span>
        </div>
        {items.length === 0 && <p className="p-6 font-editorial italic text-muted-foreground">No members yet.</p>}
        {items.map((m) => (
          <div key={m.id} className="grid grid-cols-5 gap-4 px-4 py-3 border-b border-border font-body text-sm items-center" data-testid="member-row">
            <span className="text-foreground">{m.name}</span>
            <span className="text-muted-foreground">{m.email || "—"}</span>
            <span className="text-muted-foreground">{m.phone || "—"}</span>
            <span className="text-secondary">{m.role || "Member"}</span>
            <button onClick={() => remove(m.id)} className="text-primary hover:text-secondary overline text-[0.6rem] text-right" data-testid={`remove-${m.id}`}>Remove</button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function LeaderListSubmit({ kind, token }) {
  const [items, setItems] = useState([]);
  const isEvents = kind === "events";
  const empty = isEvents
    ? { title: "", type: "Service", date: "", time: "", location: "", description: "" }
    : { title: "", body: "", audience: "Everyone", pinned: false };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leader/${kind}`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed.");
    }
  }, [kind, token]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/leader/${kind}`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Submitted for admin approval.");
      setForm(empty);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title={isEvents ? "Church Events" : "Church Announcements"}>
      <p className="font-body text-sm text-muted-foreground mb-6">Submissions require admin approval before appearing on the public site.</p>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10 max-w-3xl" data-testid={`leader-${kind}-form`}>
        {Object.keys(empty).map((k) => {
          if (k === "pinned") return null;
          if (k === "description" || k === "body") {
            return (
              <div key={k} className="md:col-span-2">
                <label className="overline text-[0.6rem] text-muted-foreground">{k}</label>
                <textarea
                  data-testid={`leader-${kind}-${k}`}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  rows={3}
                  className="w-full bg-black border-0 border-b border-white/20 focus:outline-none focus:border-primary text-foreground font-body p-2"
                />
              </div>
            );
          }
          return (
            <div key={k}>
              <label className="overline text-[0.6rem] text-muted-foreground">{k}</label>
              <Input
                data-testid={`leader-${kind}-${k}`}
                type={k === "date" ? "date" : k === "time" ? "time" : "text"}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className={cls}
                required={k === "title"}
              />
            </div>
          );
        })}
        <div className="md:col-span-2">
          <button type="submit" disabled={busy} data-testid={`submit-leader-${kind}`} className="bg-primary text-primary-foreground overline py-4 px-8 hover:bg-primary/85 disabled:opacity-60">
            {busy ? "Submitting..." : "Submit for Approval"}
          </button>
        </div>
      </form>

      <h3 className="font-display uppercase text-2xl text-foreground mb-4">Your Submissions</h3>
      <div className="border border-border">
        {items.length === 0 && <p className="p-6 font-editorial italic text-muted-foreground">No submissions yet.</p>}
        {items.map((it) => (
          <div key={it.id} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 px-4 py-3 border-b border-border" data-testid={`leader-${kind}-row`}>
            <div>
              <p className="font-body text-foreground">{it.title}</p>
              {isEvents && <p className="font-body text-xs text-muted-foreground">{it.date} · {it.location}</p>}
              {!isEvents && <p className="font-body text-xs text-muted-foreground line-clamp-1">{it.body}</p>}
            </div>
            <span className={`overline text-[0.6rem] ${it.status === "approved" ? "text-secondary" : "text-primary"}`}>{it.status || "approved"}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function ReadOnlyList({ endpoint, label, token, columns }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/${endpoint}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data)).catch(() => {});
  }, [endpoint, token]);
  return (
    <Panel title={label}>
      <p className="font-body text-sm text-muted-foreground mb-4">Network-wide view. Per-church filtering coming when forms capture church affiliation.</p>
      <div className="border border-border">
        <div className="grid gap-4 px-4 py-3 border-b border-border overline text-[0.6rem] text-secondary bg-muted/20" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
          {columns.map((c) => <span key={c}>{c}</span>)}
        </div>
        {items.length === 0 && <p className="p-6 font-editorial italic text-muted-foreground">Nothing here yet.</p>}
        {items.map((r) => (
          <div key={r.id} className="grid gap-4 px-4 py-3 border-b border-border font-body text-sm" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }} data-testid={`ro-row-${endpoint.replace(/\//g,"-")}`}>
            {columns.map((c) => <span key={c} className="text-foreground truncate">{r[c] || "—"}</span>)}
          </div>
        ))}
      </div>
    </Panel>
  );
}
